import { useContext } from "react";
import { useLocalization } from "@fluent/react";
import {
  LoadingSkeleton,
  Table,
  Tag,
  TruncatedAccount,
} from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import type { PaginatedDualApproval } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals/types";
import AccountContext from "../../contexts/AccountContext";
import styles from "../DualApproval.module.scss";
import { DUAL_APPROVALS_ROUTE } from "../routes";
import DualApprovalDrawer from "../Pending/DualApprovalDrawer";
import TemplateDualApprovalDrawer from "../Templates/TemplateDualApprovalDrawer";
import useDualApprovalDrawerNavigation from "../hooks/useDualApprovalDrawerNavigation";
import { getNameDescription } from "../utils";

type HistoryTableProps = {
  dualApprovals: PaginatedDualApproval[];
  loading: boolean;
};

const HistoryTable = ({ dualApprovals, loading }: HistoryTableProps) => {
  const { l10n } = useLocalization();
  const { accounts } = useContext(AccountContext);
  const historyPath = `/${DUAL_APPROVALS_ROUTE}/history`;

  const {
    activeDualApproval,
    showControls,
    isDrawerOpen,
    handleOpenDrawer,
    handleCloseDrawer,
    handleOnNext,
    handleOnPrev,
  } = useDualApprovalDrawerNavigation({
    items: dualApprovals,
    basePath: historyPath,
    loading,
  });

  const getTagKind = (status: string) => {
    if (status === "rejected" || status === "expired") {
      return "error";
    }
    return "success";
  };

  const renderRow = (item: PaginatedDualApproval) => {
    const getFromAccount = () => {
      const templateAccountName = item.templateVersion?.institutionAccountName;
      const templateAccountLastFour =
        item.templateVersion?.institutionAccountLastFour;

      if (templateAccountName || templateAccountLastFour) {
        return (
          <TruncatedAccount
            name={templateAccountName ?? ""}
            lastFour={templateAccountLastFour ?? ""}
          />
        );
      }

      if (item.fromAccountDisplay) {
        return item.fromAccountDisplay;
      }

      if (item.fromAccountId) {
        const account = accounts?.find(
          (a: { id: string }) => a.id === item.fromAccountId
        );

        if (account) {
          return (
            <TruncatedAccount
              name={account.name}
              lastFour={account.getMaskedNumber()}
            />
          );
        }
      }
      return null;
    };

    return (
      <Table.Row
        key={`table-row__${item.id}`}
        onRowClick={() => handleOpenDrawer(item.id)}
      >
        <Table.Cell>{Filters.longMonthDay(item.createdAt)}</Table.Cell>
        <Table.Cell>{item.displayType}</Table.Cell>
        <Table.Cell>{getNameDescription(item)}</Table.Cell>
        <Table.Cell>{getFromAccount()}</Table.Cell>
        <Table.Cell>{item.requesterName}</Table.Cell>
        <Table.Cell>
          <Tag
            kind={getTagKind(item.status)}
            label={Filters.humanize(item.status)}
          />
        </Table.Cell>
        <Table.Cell>
          <div className={styles.viewCtaContainer}>
            <span className={styles.viewCtaText}>
              {l10n.getString("button-view", {}, "View")}
            </span>
            <span className="fontSize--m narmi-icon-chevron-right" />
          </div>
        </Table.Cell>
      </Table.Row>
    );
  };

  if (loading && !isDrawerOpen) {
    return <LoadingSkeleton isLoading />;
  }

  if (dualApprovals.length === 0 && !loading && !isDrawerOpen) {
    return (
      <div className={styles.emptyState}>
        {l10n.getString(
          "heading-no-approval-history",
          {},
          "No approval history"
        )}
      </div>
    );
  }

  return (
    <>
      <div className={styles.tableScrollWrapper}>
        <Table
          colVisibility={[
            "*", // date
            "*", // type
            "*", // name/description
            "m", // originating account only on >M screens
            "m", // submitted by only on >M screens
            "m", // status only on >M screens
            "m", // view cta only on >M screens
          ]}
          colLayout={{
            s: "1fr 1fr 1fr",
            m: ".5fr .5fr 1fr 1fr .75fr .5fr auto",
            l: ".5fr .5fr 1fr 1fr .75fr .5fr auto",
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <span className={styles.uppercaseHeader}>
                  {l10n.getString("th-date")}
                </span>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <span className={styles.uppercaseHeader}>
                  {l10n.getString("th-type")}
                </span>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <span className={styles.uppercaseHeader}>
                  {l10n.getString("th-name-description")}
                </span>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <span className={styles.uppercaseHeader}>
                  {l10n.getString("th-originating-account")}
                </span>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <span className={styles.uppercaseHeader}>
                  {l10n.getString("th-submitted-by")}
                </span>
              </Table.HeaderCell>
              <Table.HeaderCell>
                <span className={styles.uppercaseHeader}>
                  {l10n.getString("th-status")}
                </span>
              </Table.HeaderCell>
              <Table.HeaderCell>{""}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{dualApprovals.map(renderRow)}</Table.Body>
        </Table>
      </div>
      <DualApprovalDrawer
        isOpen={
          isDrawerOpen && !activeDualApproval?.bulkPaymentTemplateVersionId
        }
        showControls={showControls}
        handleClose={handleCloseDrawer}
        handleOnNext={handleOnNext}
        handleOnPrev={handleOnPrev}
        dualApproval={isDrawerOpen ? activeDualApproval : null}
        drawerType="history"
        isReadOnly
        titleL10nKey="history-drawer-title"
      />
      <TemplateDualApprovalDrawer
        isOpen={
          isDrawerOpen &&
          Boolean(activeDualApproval?.bulkPaymentTemplateVersionId)
        }
        showControls={showControls}
        handleClose={handleCloseDrawer}
        handleOnNext={handleOnNext}
        handleOnPrev={handleOnPrev}
        dualApproval={isDrawerOpen ? activeDualApproval : null}
        drawerType="history"
        titleL10nKey="history-drawer-title"
      />
    </>
  );
};

export default HistoryTable;
